/* eslint-disable @typescript-eslint/no-explicit-any -- DispatchProps generic affects await dispatch() in many components */
import { Action, Dispatch } from 'redux'
import { IApi } from '../api'

// Payload of the ATLAS_AI_NOTE_WORKING websocket event, dispatched as a redux
// action. Shared by the notes / flag / generalAnalysis sagas + reducers that
// flip the "Arc is working…" indicator live on a note thread's root note.
export interface IArcNoteWorkingAction extends Action {
  data: { noteId: string; isArcWorking: boolean }
}

// Type for promise-based actions
export interface IPromiseAction extends Omit<Action, 'type'> {
  promise: (api: IApi) => Promise<any>
  type: [string, string, string]
  params?: object
  [key: string]: any
}

// Type for action creators in redux action files
// Use this to wrap action creators for proper type inference
export const createAction = <Args extends unknown[], R>(
  action: (...args: Args) => {
    promise: (api: IApi) => Promise<R>
    type: [string, string, string]
    params?: object
  },
) => action

// Mapped type to transform action creators to their dispatched (promise-returning) versions.
// Convention: API methods that are consumed through the promise middleware (apiMiddleware.ts)
// type their return as the *wrapped* shape — i.e., `Promise<{ data: T }>` — because the
// middleware re-wraps the API response under `.data` on the dispatched action. Direct callers
// of the API method (`api.X.method()`) don't exist for these endpoints, so the wrap-typing
// is purely consumer-oriented.
export type DispatchProps<T> = {
  [K in keyof T]: T[K] extends (...args: infer Args) => {
    promise: (api: IApi) => Promise<infer R>
    type: any
  }
    ? (...args: Args) => Promise<R>
    : T[K]
}

export interface ILoadingData<T> {
  isLoading: boolean
  isSaving: boolean
  isSaved: boolean
  data: T
  /**
   * The newest load this slice has started, so a reply that has been overtaken can
   * be recognised and dropped — see createLoadingDataReducer. Internal bookkeeping;
   * nothing outside the reducer reads it.
   */
  latestRequestId?: number
  /** The query the loaded rows answer. Set only by a reducer built with `pickQuery`. */
  query?: unknown
}

// Custom dispatch type that accepts both regular actions and promise actions
export type AppDispatch = (<T extends IPromiseAction | Action>(action: T) => any) & Dispatch<Action>
